import { useCallback, useEffect, useState } from 'react';
import { useMediaQuery } from '@cbhq/cds-web/hooks/useMediaQuery';
import type { ColorScheme } from '@cbhq/cds-common/core/theme';

const PERSISTED_KEY = 'vp-user-selected-theme';

function useTheme() {
  // Use CDS v8 useMediaQuery (requires MediaQueryProvider)
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const systemTheme: ColorScheme = prefersDarkMode ? 'dark' : 'light';
  const [theme, setTheme] = useState<ColorScheme>('light');

  // Workaround to prevent NextJS hydration mismatch
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedTheme: ColorScheme | undefined = window.localStorage.getItem(
        PERSISTED_KEY,
      ) as ColorScheme;
      const themeToSet = savedTheme ?? systemTheme;
      setTheme(themeToSet);
      document.body.setAttribute('data-theme', themeToSet);
      document.documentElement.style.colorScheme = themeToSet;
    }
  }, [systemTheme]);

  // Persist theme to local storage
  const toggleTheme = useCallback(() => {
    let themeToSet: ColorScheme;
    setTheme((prevTheme) => {
      if (prevTheme === 'light') {
        themeToSet = 'dark';
      } else {
        themeToSet = 'light';
      }
      window.localStorage.setItem(PERSISTED_KEY, themeToSet);
      document.documentElement.style.colorScheme = themeToSet;
      return themeToSet;
    });
  }, []);

  return { theme, setTheme, toggleTheme };
}

export { useTheme };
